import { formatPln } from '../catalog/money';

/** How a transport line got its price (P5), as the API sends it; the amounts only for whoever sees the price. */
export type LineTransport = {
  kind: 'truck' | 'car';
  km: number | null;
  /** The method applied: "Ryczałt" or "Wg km". */
  mode: 'flat' | 'distance';
  /** Owner 2026-10-23: the method chosen by hand (null: by the threshold of "Ustawienia transportu"). */
  chosenMode?: 'flat' | 'distance' | null;
  /** The method by the threshold for these km. */
  defaultMode?: 'flat' | 'distance';
  billedKm: number;
  localKm: number;
  roundTrip: boolean;
  flatGrosze?: number;
  baseGrosze?: number;
  perKmGrosze?: number;
};

/** How the km are counted: "2 × 312 km" there and back, else "312 km w jedną stronę". */
const kmWay = (km: number, roundTrip: boolean) => (roundTrip ? `2 × ${km} km` : `${km} km w jedną stronę`);

/** Owner 2026-10-23 (no straight line): a transport without km says what to do, never silently. */
export const NO_KM = 'brak km — wpisz km wg trasy w wydarzeniu';

/**
 * Shown under the line's name, short (owner 2026-10-23 10:34): "ok. 30 km · ryczałt (do 50 km)" or "ok. 312 km ·
 * wg km: 2 × 312 km"; `detail`: the calculation ("300,00 zł + 624 km × 3,00 zł") for the tooltip; `chosen`: the
 * method was chosen by hand.
 */
export function transportNote(t: LineTransport): { km: string | null; how: string; detail: string; chosen: boolean } {
  const chosen = (t.chosenMode ?? null) !== null;
  if (t.km === null) return { km: null, how: `ryczałt · ${NO_KM}`, detail: '', chosen };
  if (t.mode === 'flat') return { km: `ok. ${t.km} km`, how: chosen ? 'ryczałt' : `ryczałt (do ${t.localKm} km)`, detail: '', chosen };
  const base = t.baseGrosze !== undefined ? formatPln(t.baseGrosze) : 'kwota bazowa';
  const rate = t.perKmGrosze !== undefined ? formatPln(t.perKmGrosze) : 'stawka';
  // review M3: "ok. 312 km · wg km: 2 × 312 km"; one way said so
  return { km: `ok. ${t.km} km`, how: `wg km: ${kmWay(t.km, t.roundTrip)}`, detail: `${base} + ${t.billedKm} km × ${rate}`, chosen };
}

/**
 * A transport of the price list as "+ Transport" offers it in a draft (`GET /api/quotes/versions/:id/transport-options`):
 * priced with the event's km now and the threshold. `km` and `mode` only for whoever sees the event's distance,
 * `priceGrosze` only for whoever sees the lines' prices.
 */
export type TransportOption = { serviceId: string; kind: 'truck' | 'car'; km?: number | null; mode?: 'flat' | 'distance'; billedKm?: number; localKm?: number; roundTrip?: boolean; priceGrosze?: number };

/**
 * Owner 2026-10-23 10:32 („powyżej progu pokazuj nie ryczałt tylko wg km”): the option with the price it will get —
 * "ryczałt 300,00 zł", "wg km: 2 × 400 km = 2 700,00 zł", or without km "ryczałt 300,00 zł · brak km — wpisz km wg trasy".
 */
export function transportOptionLabel(name: string, o: TransportOption | undefined): string {
  // review M2: until the options come, only the name — never the flat amount as a guess
  if (!o) return name;
  const price = o.priceGrosze !== undefined ? formatPln(o.priceGrosze) : null;
  if (o.mode === undefined || o.km === undefined) return price ? `${name} · ${price}` : name;
  if (o.km === null || o.mode === 'flat') {
    const flat = `${name} · ryczałt${price ? ` ${price}` : ''}`;
    return o.km === null ? `${flat} · brak km — wpisz km wg trasy` : flat;
  }
  return `${name} · wg km: ${kmWay(o.km, o.roundTrip ?? true)}${price ? ` = ${price}` : ''}`;
}

/**
 * Owner 2026-10-23: a sent version keeps its transport when the event's km change; it says so (a new version counts
 * the transport again).
 */
export function transportKmNowText(v: { transportKmNow?: number | null | undefined; sections: Array<{ lines: Array<{ transport?: LineTransport | null }> }> }): string {
  const counted = v.sections.flatMap((s) => s.lines).find((l) => l.transport)?.transport?.km ?? null;
  const now = v.transportKmNow === null || v.transportKmNow === undefined ? 'brak odległości' : `${v.transportKmNow} km`;
  return `Odległość wydarzenia zmieniła się: teraz ${now}${counted !== null ? ` (w tej wersji ${counted} km)` : ''}. Ta wersja zostaje bez zmian — nowa wersja policzy transport od nowa.`;
}
